import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, ArrowUpRight, Bookmark, BookmarkCheck, Loader2, Search as SearchIcon, BookOpen } from "lucide-react";

const TAB_KEYS = [
    { key: "plain", label: "Plain Language" },
    { key: "analysis", label: "Legal Analysis" },
    { key: "case_law", label: "Case Law" },
    { key: "current_affairs", label: "Current Affairs" },
];

export default function TreatyExplorer() {
    const { slug, num } = useParams();
    if (slug && num) return <ArticleView slug={slug} num={num} />;
    return <TreatyTreeView preselectSlug={slug} />;
}

function TreatyTreeView({ preselectSlug }) {
    const [treaties, setTreaties] = useState([]);
    const [slug, setSlug] = useState(preselectSlug || "tfeu");
    const [tree, setTree] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [searchQ, setSearchQ] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [bookmarkIds, setBookmarkIds] = useState(new Set());

    useEffect(() => {
        api.get("/treaties").then(({ data }) => setTreaties(data.items));
        refreshBookmarks();
    }, []);
    useEffect(() => {
        api.get(`/treaties/${slug}`).then(({ data }) => {
            setTree(data.tree || []);
            setBookmarkIds(new Set(data.bookmark_ids || []));
        });
    }, [slug]);

    const refreshBookmarks = () => {
        api.get("/treaties/bookmarks").then(({ data }) => setBookmarks(data.items || []));
    };
    const runSearch = async (e) => {
        e?.preventDefault();
        const q = searchQ.trim();
        if (!q) { setSearchResults(null); return; }
        const { data } = await api.get(`/treaties/search?q=${encodeURIComponent(q)}`);
        setSearchResults(data.items || []);
    };

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="treaty-explorer">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">Treaty Explorer</h1>
            <p className="mt-3 text-[#0D1B3E]/75 max-w-2xl">
                Browse the TEU, TFEU and Charter. Open any article for plain-language,
                analytical, case-law and current-affairs perspectives, and compare
                across Member States.
            </p>

            <div className="mt-8 inline-flex border border-[#0D1B3E]/20 bg-white" data-testid="treaty-tabs">
                {treaties.map((t) => (
                    <button
                        key={t.slug}
                        type="button"
                        onClick={() => { setSlug(t.slug); setSearchResults(null); setSearchQ(""); }}
                        data-testid={`treaty-tab-${t.slug}`}
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] ${
                            slug === t.slug ? "bg-[#0D1B3E] text-[#F5F0E8]" : "text-[#0D1B3E]/80 hover:bg-[#0D1B3E]/5"
                        }`}
                    >
                        {t.abbr}
                    </button>
                ))}
            </div>

            <form onSubmit={runSearch} className="mt-5 max-w-xl relative" data-testid="treaty-search-form">
                <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Search articles by number, title, or topic…"
                    className="field pl-9"
                    data-testid="treaty-search-input"
                />
            </form>

            {searchResults ? (
                <div className="mt-8" data-testid="treaty-search-results">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-2xl tracking-tight">Search results</h2>
                        <button onClick={() => { setSearchQ(""); setSearchResults(null); }}
                                className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                                data-testid="treaty-search-clear">
                            Clear
                        </button>
                    </div>
                    <ul className="mt-4 space-y-2">
                        {searchResults.map((a) => <ArticleRow key={a.id} a={a} />)}
                    </ul>
                </div>
            ) : (
                <div className="mt-8 grid gap-8 lg:grid-cols-3">
                    <div className="lg:col-span-2 space-y-7" data-testid="treaty-tree">
                        {tree.map((g) => (
                            <section key={g.label}>
                                <div className="label-eyebrow">{g.label}</div>
                                <ul className="mt-3 space-y-1.5">
                                    {g.articles.map((a) => (
                                        <ArticleRow key={a.id} a={a} bookmarked={bookmarkIds.has(a.id)} />
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                    <aside data-testid="treaty-bookmarks-panel">
                        <div className="label-eyebrow">Your bookmarks</div>
                        {bookmarks.length === 0 ? (
                            <p className="mt-3 text-sm text-[#0D1B3E]/60">
                                Bookmark any article from its detail page.
                            </p>
                        ) : (
                            <ul className="mt-3 space-y-1.5">
                                {bookmarks.map((a) => <ArticleRow key={a.id} a={a} bookmarked />)}
                            </ul>
                        )}
                    </aside>
                </div>
            )}
        </div>
    );
}

function ArticleRow({ a, bookmarked }) {
    return (
        <li>
            <Link
                to={`/app/treaty/${a.treaty}/article/${a.number}`}
                className="flex items-baseline gap-3 py-1.5 hover:text-[#C9A84C]"
                data-testid={`treaty-article-row-${a.treaty}-${a.number}`}
            >
                <span className="font-mono text-xs text-[#C9A84C] w-12 shrink-0">{a.treaty.toUpperCase()} {a.number}</span>
                <span className="text-sm text-[#0D1B3E] leading-snug truncate">{a.title}</span>
                {bookmarked && <BookmarkCheck size={12} className="text-[#C9A84C] ml-auto shrink-0" />}
            </Link>
        </li>
    );
}

function ArticleView({ slug, num }) {
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("plain");
    const [tabLoading, setTabLoading] = useState(false);
    const [error, setError] = useState("");
    const [memberStates, setMemberStates] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [compareLoading, setCompareLoading] = useState(false);
    const [comparison, setComparison] = useState(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/treaties/${slug}/articles/${num}`),
            api.get("/treaties"),
        ]).then(([{ data: a }, { data: t }]) => {
            setArticle(a);
            setMemberStates(t.member_states);
        }).catch(() => setArticle(null)).finally(() => setLoading(false));
    }, [slug, num]);

    const tabContent = article?.tabs?.[activeTab];

    const generateTab = async () => {
        setTabLoading(true);
        setError("");
        try {
            const { data } = await api.post(`/treaties/${slug}/articles/${num}/tabs`, { tab: activeTab });
            setArticle((a) => ({ ...a, tabs: { ...(a.tabs || {}), [activeTab]: data.content } }));
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Could not generate this tab.");
        } finally {
            setTabLoading(false);
        }
    };

    const toggleBookmark = async () => {
        const { data } = await api.post("/treaties/bookmarks", { article_id: article.id });
        setArticle({ ...article, bookmarked: data.bookmarked });
    };

    const toggleState = (s) => setSelectedStates((cur) =>
        cur.includes(s) ? cur.filter((x) => x !== s) : cur.length < 6 ? [...cur, s] : cur
    );
    const runCompare = async () => {
        setCompareLoading(true);
        setError("");
        try {
            const { data } = await api.post(`/treaties/${slug}/articles/${num}/compare`, {
                member_states: selectedStates,
            });
            setComparison(data.comparison);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Comparison failed.");
        } finally {
            setCompareLoading(false);
        }
    };

    if (loading) return <div className="container-edit py-10"><Loader2 className="animate-spin" /></div>;
    if (!article) return <div className="container-edit py-10">Article not found.</div>;

    return (
        <div className="container-edit py-10 lg:py-14 max-w-5xl" data-testid="treaty-article-view">
            <button
                type="button"
                onClick={() => navigate(`/app/treaty/${slug}`)}
                className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
            >
                <ArrowLeft size={14} /> Back to {slug.toUpperCase()}
            </button>
            <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#C9A84C]">
                        {slug.toUpperCase()} · Article {article.number}
                    </div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight" data-testid="treaty-article-title">
                        {article.title}
                    </h1>
                    <p className="mt-1 text-sm text-[#0D1B3E]/65">{article.group}{article.subgroup ? ` · ${article.subgroup}` : ""}</p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={toggleBookmark}
                        className="btn-secondary"
                        data-testid="treaty-article-bookmark"
                    >
                        {article.bookmarked ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                        {article.bookmarked ? "Bookmarked" : "Bookmark"}
                    </button>
                    {article.eur_lex_url && (
                        <a href={article.eur_lex_url} target="_blank" rel="noreferrer" className="btn-secondary">
                            EUR-Lex <ArrowUpRight size={14} />
                        </a>
                    )}
                </div>
            </div>
            {article.hint && (
                <p className="mt-5 italic text-[#0D1B3E]/75">{article.hint}</p>
            )}

            <div className="mt-8 border-b border-[#0D1B3E]/15 flex flex-wrap" data-testid="treaty-article-tabs">
                {TAB_KEYS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setActiveTab(t.key)}
                        data-testid={`treaty-article-tab-${t.key}`}
                        className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                            activeTab === t.key
                                ? "border-[#C9A84C] text-[#0D1B3E]"
                                : "border-transparent text-[#0D1B3E]/55 hover:text-[#0D1B3E]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="mt-6" data-testid="treaty-article-tab-content">
                {tabContent ? (
                    <p className="whitespace-pre-wrap text-base leading-relaxed">{tabContent}</p>
                ) : (
                    <div>
                        <p className="text-sm text-[#0D1B3E]/65">
                            Content for this tab has not been generated yet. Click below to
                            generate it (1 credit).
                        </p>
                        <button
                            type="button"
                            onClick={generateTab}
                            disabled={tabLoading}
                            className="btn-primary mt-3 disabled:opacity-50"
                            data-testid="treaty-article-tab-generate"
                        >
                            {tabLoading ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />}
                            Generate {TAB_KEYS.find((x) => x.key === activeTab)?.label}
                        </button>
                    </div>
                )}
            </div>

            <section className="mt-14" data-testid="treaty-article-compare">
                <div className="label-eyebrow">Member State comparison</div>
                <h2 className="font-display text-2xl mt-2 tracking-tight">Compare across jurisdictions</h2>
                <p className="text-sm text-[#0D1B3E]/65 mt-1">
                    Pick 2–6 Member States. 3 credits.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {memberStates.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => toggleState(s)}
                            data-testid={`treaty-compare-ms-${s}`}
                            className={`px-2.5 py-1 text-xs border transition-colors ${
                                selectedStates.includes(s)
                                    ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                    : "border-[#0D1B3E]/20 text-[#0D1B3E]/80 hover:bg-[#0D1B3E]/5"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={runCompare}
                    disabled={compareLoading || selectedStates.length < 2}
                    className="btn-primary mt-4 disabled:opacity-50"
                    data-testid="treaty-compare-run"
                >
                    {compareLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                    Compare {selectedStates.length} Member States
                </button>
                {error && <p className="text-sm text-red-700 mt-3" data-testid="treaty-error">{error}</p>}

                {comparison && (
                    <div className="mt-8 overflow-x-auto" data-testid="treaty-compare-result">
                        {comparison.question && <p className="italic mb-4">{comparison.question}</p>}
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr>
                                    <th className="border-b border-[#0D1B3E]/15 py-2 px-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                        Dimension
                                    </th>
                                    {selectedStates.map((s) => (
                                        <th key={s} className="border-b border-[#0D1B3E]/15 py-2 px-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                            {s}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {(comparison.rows || []).map((r, i) => (
                                    <tr key={i} className="align-top">
                                        <td className="border-b border-[#0D1B3E]/10 py-3 px-3 font-display">{r.dimension}</td>
                                        {selectedStates.map((s) => (
                                            <td key={s} className="border-b border-[#0D1B3E]/10 py-3 px-3">
                                                {r.by_state?.[s] || "–"}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {comparison.synthesis?.length > 0 && (
                            <div className="mt-6">
                                <div className="label-eyebrow">Synthesis</div>
                                <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
                                    {comparison.synthesis.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
